import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  Snackbar,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

import { useAtomValue, useSetAtom } from "jotai";
import { AppAtoms } from "../lib/store";

import { getEntryAnalysys, getEntryAnalysysMultiDivision } from "../api/entryAnalysys";
import { getTossUpAnalysys, getTossUpAnalysysMultiDivision } from "../api/tossUpAnalysys";
import {
  getDashboard,
  getDashboardDailyDetail,
  getEmptyDashboardResponse,
  DashboardResponse,
  DashboardDailyDetailResponse,
} from "../api/dashboard";
import {
  getContractProducts,
  postContract,
  postContractShortMessage,
} from "../api/contract";
import { postTossUp, getTossUpAddBefore } from "../api/tossUp";
import { getSerchAccountCollaborateCompanys } from "../api/serch";

import { CONST } from "../config/const";
import { entryAnalysysTableColumn } from "../config/entryAnalysys";
import { tossUpAnalysysTableColumn } from "../config/tossUpAnalysys";
import { contractTableColumn } from "../config/contract";
import { dataDifine } from "../config/dataInterface";

import { getFirstYear, getOneYear } from "../lib/other";
import {
  getDaysElapsed,
  getDaysInMonth,
  getProratedTotal,
} from "../lib/dashboardKpi";
import { makeParams } from "../api/common";
import TableTopBar from "../component/TableTopBar";
import AiChatSideBar from "../component/AiChatSideBar";
import {
  AnalysisDetailModal,
  AnalysisDetailModalType,
} from "../component/AnalysisDetailModal";
import { CustomizedYAxisTick } from "../component/Charts";
import { DailyCalendar } from "../component/DailyCalendar";
import { ContractAddModal } from "../component/ContractAddModal";
import { TossUpAddModal } from "../component/TossUpAddModal";
import { ChangeAccountCollaborateModal } from "../component/tableFunction/ChangeAccountCollaborateModal";
import { DataAddModalResult } from "../component/DataAddModalResult";

function getSixMonthsAgo(): { year: number; month: number } {
  const d = new Date();
  d.setMonth(d.getMonth() - 6);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

const colorPlette = ["#FF4B00", "#005AFF", "#03AF7A", "#4DC4FF", "#F6AA00"];

function sumRowForTrend(row: dataDifine): number {
  return Object.entries(row).reduce(
    (s, [k, v]) =>
      k !== "name" &&
      !k.endsWith("_sub_column") &&
      k !== "sub_count_column_name" &&
      typeof v === "number"
        ? s + v
        : s,
    0,
  );
}

function toTrendData(analysys: dataDifine[]): { name: string; 合計: number }[] {
  return (analysys || []).map((r) => ({
    name: String(r.name ?? ""),
    合計: sumRowForTrend(r),
  }));
}

/** エントリー集計項目: エントリー数 | 開通数 | 解約数 */
export type EntryAggregationItem = "エントリー数" | "開通数" | "解約数";

function buildEntryParams(
  division: string,
  countType: string,
  aggregationItem: EntryAggregationItem,
  priceType: "ショット" | "ストック",
  startYear: number,
  startMonth: number,
  endYear: number,
  endMonth: number,
  granularity: "日別" | "月別",
) {
  const base = entryAnalysysTableColumn.dataColumn.reduce(
    (acc, d) => {
      if (!d.serch) return acc;
      if (d.serchType === "analysys_year_month") {
        return {
          ...acc,
          analysys_granularity_month_start_year: String(startYear),
          analysys_granularity_month_start_month: String(startMonth).padStart(
            2,
            "0",
          ),
          analysys_granularity_month_end_year: String(endYear),
          analysys_granularity_month_end_month: String(endMonth).padStart(
            2,
            "0",
          ),
          analysys_granularity_date_year: String(endYear),
          analysys_granularity_date_month: String(endMonth).padStart(2, "0"),
        };
      }
      if (d.key === "analysys_granularity")
        return { ...acc, [d.key]: granularity };
      if (
        d.key === "entry_analysys_division_admin" ||
        d.key === "entry_analysys_division_partner"
      )
        return { ...acc, [d.key]: division };
      if (d.key === "entry_analysys_count_type")
        return { ...acc, [d.key]: countType };
      if (d.key === "entry_aggregation_item")
        return { ...acc, [d.key]: aggregationItem };
      if (d.key === "entry_analysys_sub_count")
        return { ...acc, [d.key]: "クロージング件数" };
      if (d.key === "entry_analysys_price_type")
        return { ...acc, [d.key]: priceType };
      return { ...acc, [d.key]: "" };
    },
    {} as { [key: string]: string },
  );
  return makeParams(entryAnalysysTableColumn.dataColumn, base);
}

function buildTossUpParams(
  divisionAdmin: string,
  divisionPartner: string,
  countType: string,
  subCount: string,
  startYear: number,
  startMonth: number,
  endYear: number,
  endMonth: number,
  granularity: "日別" | "月別",
) {
  const base = tossUpAnalysysTableColumn.dataColumn.reduce(
    (acc, d) => {
      if (!d.serch) return acc;
      if (d.serchType === "analysys_year_month") {
        return {
          ...acc,
          analysys_granularity_month_start_year: String(startYear),
          analysys_granularity_month_start_month: String(startMonth).padStart(
            2,
            "0",
          ),
          analysys_granularity_month_end_year: String(endYear),
          analysys_granularity_month_end_month: String(endMonth).padStart(
            2,
            "0",
          ),
          analysys_granularity_date_year: String(endYear),
          analysys_granularity_date_month: String(endMonth).padStart(2, "0"),
        };
      }
      if (d.key === "analysys_granularity")
        return { ...acc, [d.key]: granularity };
      if (d.key === "toss_up_analysys_division_admin")
        return { ...acc, [d.key]: divisionAdmin };
      if (d.key === "toss_up_analysys_division_partner")
        return { ...acc, [d.key]: divisionPartner };
      if (d.key === "toss_up_aggregation_item")
        return { ...acc, [d.key]: subCount };
      if (d.key === "toss_up_analysys_sub_count")
        return { ...acc, [d.key]: CONST.dashboard_tossup_sub_count_initial };
      return { ...acc, [d.key]: "" };
    },
    {} as { [key: string]: string },
  );
  const params = makeParams(tossUpAnalysysTableColumn.dataColumn, base);
  return {
    ...params,
    toss_up_analysys_count_type: countType,
    toss_up_analysys_division_admin: divisionAdmin,
    toss_up_analysys_division_partner: divisionPartner,
  };
}

function Dashboard() {
  const setIsLoading = useSetAtom(AppAtoms.isLoading);
  const loginUser = useAtomValue(AppAtoms.loginUser);
  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);
  const { thisYear, thisMonth } = getOneYear();
  const daysElapsed = getDaysElapsed();
  const daysInMonth = getDaysInMonth();

  const [firstLoading, setFirstLoading] = useState(true);
  const [dashboardKpi, setDashboardKpi] = useState<DashboardResponse | null>(
    null,
  );
  const [kpiLoading, setKpiLoading] = useState(true);
  const [graphLoading, setGraphLoading] = useState(true);

  /** トスアップ: グラフの集計項目（KPIカード選択と連動） */
  const [selectedTossUpSubCount, setSelectedTossUpSubCount] =
    useState<string>("トスアップ数");
  /** クロージング: グラフの集計項目 */
  const [selectedClosingSubCount, setSelectedClosingSubCount] =
    useState<string>("クロージング-有効アポ数");
  /** エントリー: 集計項目と単価種類（グラフと連動） */
  const [selectedEntryAggregation, setSelectedEntryAggregation] =
    useState<EntryAggregationItem>("エントリー数");
  const [selectedEntryPriceType, setSelectedEntryPriceType] = useState<
    "ショット" | "ストック"
  >("ショット");

  const [entryShotByProduct, setEntryShotByProduct] = useState<dataDifine[]>(
    [],
  );
  const [entryShotByPartner, setEntryShotByPartner] = useState<dataDifine[]>(
    [],
  );
  const [entryShotByAccount, setEntryShotByAccount] = useState<dataDifine[]>(
    [],
  );
  const [entryShotTrendDaily, setEntryShotTrendDaily] = useState<dataDifine[]>(
    [],
  );
  const [entryShotTrendMonthly, setEntryShotTrendMonthly] = useState<
    dataDifine[]
  >([]);

  const [tossUpByPartner, setTossUpByPartner] = useState<dataDifine[]>([]);
  const [tossUpByAccount, setTossUpByAccount] = useState<dataDifine[]>([]);
  const [closingByPartner, setClosingByPartner] = useState<dataDifine[]>([]);
  const [closingByAccount, setClosingByAccount] = useState<dataDifine[]>([]);
  const [closingTrendDaily, setClosingTrendDaily] = useState<dataDifine[]>([]);
  const [closingTrendMonthly, setClosingTrendMonthly] = useState<dataDifine[]>(
    [],
  );
  const [tossUpTrendDaily, setTossUpTrendDaily] = useState<dataDifine[]>([]);
  const [tossUpTrendMonthly, setTossUpTrendMonthly] = useState<dataDifine[]>(
    [],
  );
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  /** グラフとKPI一致確認用: トスアップ・クロージングの期間合計（all_data の合計） */
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailModalType, setDetailModalType] =
    useState<AnalysisDetailModalType>("entry");
  const [detailModalInitialParams, setDetailModalInitialParams] = useState<{
    [key: string]: string;
  } | null>(null);
  const [detailModalPreloadedData, setDetailModalPreloadedData] = useState<{
    analysys: dataDifine[];
    columns: string[];
    all_data: dataDifine[];
  } | null>(null);
  const [serchOpen, setSerchOpen] = useState(false);
  const [aiChatOpen, setAiChatOpen] = useState(false);
  /** 案D: どのセクションのグラフを表示するか */
  const [selectedSectionForD, setSelectedSectionForD] = useState<
    "tossup" | "closing" | "entry"
  >("entry");

  /** カレンダー表示用: 表示中の年月 */
  const [calendarViewYear, setCalendarViewYear] = useState<number>(
    new Date().getFullYear(),
  );
  const [calendarViewMonth, setCalendarViewMonth] = useState<number>(
    new Date().getMonth() + 1,
  );
  /** カレンダー表示用: 当月以外のデータ（月ナビゲーション時にfetch） */
  const [calendarOverrideData, setCalendarOverrideData] = useState<
    { name: string; 合計: number }[] | null
  >(null);
  const [calendarDataLoading, setCalendarDataLoading] = useState(false);

  /** KPI月選択: 「今月の実績」用 */
  const [selectedKpiYear, setSelectedKpiYear] = useState<number>(
    new Date().getFullYear(),
  );
  const [selectedKpiMonth, setSelectedKpiMonth] = useState<number>(
    new Date().getMonth() + 1,
  );
  /** KPI日付選択: 「本日の実績」用 */
  const [selectedKpiDate, setSelectedKpiDate] = useState<Date>(new Date());

  /** 商材ドリルダウン */
  const [drillDownOpen, setDrillDownOpen] = useState(false);
  const [drillDownProductName, setDrillDownProductName] = useState<string>("");
  const [drillDownByPartner, setDrillDownByPartner] = useState<dataDifine[]>(
    [],
  );
  const [drillDownByAccount, setDrillDownByAccount] = useState<dataDifine[]>(
    [],
  );
  const [drillDownDailyData, setDrillDownDailyData] = useState<
    { name: string; 合計: number }[]
  >([]);
  const [drillDownLoading, setDrillDownLoading] = useState(false);

  /** 日付クリック詳細ダイアログ */
  const [dayDetailOpen, setDayDetailOpen] = useState(false);
  const [dayDetailDate, setDayDetailDate] = useState<string>("");
  const [dayDetailData, setDayDetailData] =
    useState<DashboardDailyDetailResponse | null>(null);
  const [dayDetailLoading, setDayDetailLoading] = useState(false);

  const isTossUpAccount = loginUser?.db_authority === "トスアップアカウント";

  const isEntryAnalysisAvailable = !isTossUpAccount;
  const isClosingAvailable = !isTossUpAccount;

  useEffect(() => {
    if (!isEntryAnalysisAvailable && selectedSectionForD === "entry") {
      setSelectedSectionForD("tossup");
    }
    if (!isClosingAvailable && selectedSectionForD === "closing") {
      setSelectedSectionForD("tossup");
    }
  }, [isEntryAnalysisAvailable, isClosingAvailable, selectedSectionForD]);

  // --- エントリー/トスアップ追加モーダル用 state ---
  const canAddEntry =
    loginUser?.authority === "パートナー" ||
    loginUser?.authority === "営業アカウント";

  const firstYear = getFirstYear();

  // エントリー追加用
  const [entryAddData, setEntryAddData] = useState<{
    addStatus:
      | null
      | "loading"
      | "change_account"
      | "product"
      | "entry_method"
      | "site_path"
      | "contract"
      | "sms_success";
    outSiteEntry: 0 | 1;
    outsideSiteUrl: string;
    outsideServiceUrl: string;
    viewOutsideServiceUrl: 0 | 1;
    productId: number | null;
    productName: string;
    have_entry_form: number | null;
    site_path_list: string[];
    site_path: string;
    outsideMessageType: number | null;
    phone_number: string;
    customer_name: string;
    customer_company_name: string;
    servicePlanName: string;
    serviceContentNameList: string[];
    serviceCampaignPulldownName: string;
    serviceCampaignCheckboxName: string[];
    servicePaymentMethodName: string;
    serviseCustomerListID: string;
    pullDownServiceName: string;
    checkBoxServiceNames: string;
    smsMessagePatternID: number | null;
    customerInfo: { [key: string]: string | string[] };
    changeAccount: 0 | 1;
    changeAccountID: number | null;
  }>({
    addStatus: null,
    outSiteEntry: 0,
    outsideSiteUrl: "",
    outsideServiceUrl: "",
    viewOutsideServiceUrl: 0,
    productId: null,
    productName: "",
    have_entry_form: null,
    site_path_list: [],
    site_path: "",
    outsideMessageType: null,
    phone_number: "",
    customer_name: "",
    customer_company_name: "",
    servicePlanName: "",
    serviceContentNameList: [],
    serviceCampaignPulldownName: "",
    serviceCampaignCheckboxName: [],
    servicePaymentMethodName: "",
    serviseCustomerListID: "",
    pullDownServiceName: "",
    checkBoxServiceNames: "",
    smsMessagePatternID: null,
    customerInfo: {},
    changeAccount: 0,
    changeAccountID: null,
  });
  useEffect(() => {
    if (entryAddData.addStatus === "loading") {
      setIsLoading(true);
    } else if (!firstLoading) {
      setIsLoading(false);
    }
  }, [entryAddData]);
  const [entryProductList, setEntryProductList] = useState<
    {
      id: number;
      product_name: string;
      have_entry_form: number;
      site_path_list: string[];
    }[]
  >([]);
  const [entrySelectServisePlanList, setEntrySelectServisePlanList] = useState<
    { service_name: string }[]
  >([]);
  const [entrySelectServiseContentList, setEntrySelectServiseContentList] =
    useState<{ service_name: string }[]>([]);
  const [
    entrySelectServiseCampaignPulldownList,
    setEntrySelectServiseCampaignPulldownList,
  ] = useState<{ service_name: string }[]>([]);
  const [
    entrySelectServiseCampaignCheckboxList,
    setEntrySelectServiseCampaignCheckboxList,
  ] = useState<{ service_name: string }[]>([]);
  const [entrySelectServisePaymentList, setEntrySelectServisePaymentList] =
    useState<{ payment_method: string }[]>([]);
  const [entryUniqueOptinList, setEntryUniqueOptinList] = useState<
    { [key: string]: string | number | string[] }[]
  >([]);
  const [entryAddressOptionName, setEntryAddressOptionName] = useState("");
  const [entryPostPictures, setEntryPostPicture] = useState<{
    [key: string]: File;
  }>({});

  // トスアップ追加用
  const [tossUpAddData, setTossUpAddData] = useState<{
    addStatus:
      | null
      | "loading"
      | "change_account"
      | "product"
      | "toss_up"
      | "success"
      | "failed";
    category_id_list: number[];
    product_name_list: string[];
    phone_number: string;
    customer_name: string;
    customer_list_id: string;
    hope_call_date: string;
    hope_call_time_kind: number | null;
    hope_call_time: string;
    customerInfo: { [key: string]: string | string[] };
    changeAccount: 0 | 1;
    changeAccountID: number | null;
  }>({
    addStatus: null,
    category_id_list: [],
    product_name_list: [],
    phone_number: "",
    customer_name: "",
    customer_list_id: "",
    hope_call_date: "",
    hope_call_time_kind: null,
    hope_call_time: "12-00",
    customerInfo: {},
    changeAccount: 0,
    changeAccountID: null,
  });

  useEffect(() => {
    if (tossUpAddData.addStatus === "loading") {
      setIsLoading(true);
    } else if (!firstLoading) {
      setIsLoading(false);
    }
  }, [tossUpAddData]);

  const [tossUpCategoryNameList, setTossUpCategoryNameList] = useState<
    { category_id: number; category_name: string }[]
  >([]);
  const [tossUpProductNameList, setTossUpProductNameList] = useState<
    { category_id: number; category_name: string; product_name: string }[]
  >([]);
  const [tossUpUniqueOptinList, setTossUpUniqueOptinList] = useState<
    { [key: string]: string | number | string[] }[]
  >([]);
  const [tossUpAddressOptionName, setTossUpAddressOptionName] = useState("");
  const [tossUpPostPictures, setTossUpPostPicture] = useState<{
    [key: string]: File;
  }>({});

  // 共通: 協業アカウント一覧 + 結果表示
  const [
    accountCollaborateCompanySaleList,
    setAccountCollaborateCompanySaleList,
  ] = useState<
    {
      sale_company_id: number;
      sale_company_name: string;
      id: number;
      account_name: string;
    }[]
  >([]);
  const [onDataAddModalResult, setOnDataAddModalResult] = useState("");
  // どちらのモーダルを開いているか
  const [dashboardAddType, setDashboardAddType] = useState<
    "entry" | "tossup" | null
  >(null);
  const [addSuccessSnackbar, setAddSuccessSnackbar] = useState<string | null>(
    null,
  );

  // 協業アカウント一覧取得（初回）
  useEffect(() => {
    if (canAddEntry) {
      (async () => {
        const fetchData = await getSerchAccountCollaborateCompanys();
        if (fetchData) {
          setAccountCollaborateCompanySaleList(
            fetchData.accountCollaborateCompanySales as {
              sale_company_id: number;
              sale_company_name: string;
              id: number;
              account_name: string;
            }[],
          );
        }
      })();
    }
  }, [canAddEntry]);

  // エントリー追加ボタンクリック
  function handleOpenEntryAdd() {
    setDashboardAddType("entry");
    setEntryAddData({
      ...entryAddData,
      addStatus: "change_account",
    });
  }

  // トスアップ追加ボタンクリック
  function handleOpenTossUpAdd() {
    setDashboardAddType("tossup");
    setTossUpAddData({
      ...tossUpAddData,
      addStatus: "change_account",
    });
  }

  // エントリー: 商材一覧取得（ChangeAccountCollaborateModalから呼ばれる）
  async function getEntryProductListForDashboard() {
    setEntryAddData((prev) => ({ ...prev, addStatus: "loading" }));
    const productList = await getContractProducts(
      entryAddData.changeAccount,
      entryAddData.changeAccountID,
    );
    if (productList) {
      setEntryProductList(productList);
      setEntryAddData((prev) => ({ ...prev, addStatus: "product" }));
    } else {
      closeEntryAddModal();
      setOnDataAddModalResult(
        "エントリー可能な商材が定義されていません。管理者に問い合わせてください。",
      );
    }
  }

  // トスアップ: 商材一覧取得
  async function getTossUpProductListForDashboard() {
    setTossUpAddData((prev) => ({ ...prev, addStatus: "loading" }));
    const { tossUpCategoryNameList: catList, tossUpProductNameList: prodList } =
      await getTossUpAddBefore(
        tossUpAddData.changeAccount,
        tossUpAddData.changeAccountID,
      );
    if (catList.length > 0) {
      setTossUpCategoryNameList(catList);
      setTossUpProductNameList(prodList);
      setTossUpAddData((prev) => ({ ...prev, addStatus: "product" }));
    } else {
      closeTossUpAddModal();
      setOnDataAddModalResult(
        "トスアップ可能な商材が定義されていません。管理者に問い合わせてください。",
      );
    }
  }

  // エントリー: 登録処理
  async function postEntryAddData(customerInfo: {
    [key: string]: string | string[];
  }) {
    if (entryAddData.outSiteEntry == 0) {
      const postResponce = await postContract({
        ...entryAddData,
        customerInfo,
      });
      if (postResponce === "success") {
        closeEntryAddModal();
        setAddSuccessSnackbar("エントリーを登録しました");
        setRefreshTrigger((prev) => prev + 1);
      } else {
        setOnDataAddModalResult(postResponce);
        setEntryAddData((prev) => ({ ...prev, addStatus: "contract" }));
      }
    } else if (entryAddData.outSiteEntry == 1) {
      let mail_address = "@";
      entryUniqueOptinList.map((uniqueOptin) => {
        if (uniqueOptin.mail_address_flg === 1) {
          mail_address = entryAddData.customerInfo[
            uniqueOptin.option_name as string
          ] as string;
        }
      });
      if (mail_address === "@" && entryAddData.customerInfo["メールアドレス"]) {
        mail_address = entryAddData.customerInfo["メールアドレス"] as string;
      }
      const {
        postResponce,
        outsideSiteUrl,
        outsideServiceUrl,
        viewOutsideServiceUrl,
      } = await postContractShortMessage(
        { ...entryAddData, customerInfo },
        mail_address,
        "",
        "",
      );
      if (postResponce === "success") {
        setEntryAddData((prev) => ({
          ...prev,
          addStatus: "sms_success",
          outsideSiteUrl,
          outsideServiceUrl,
          viewOutsideServiceUrl,
        }));
        setRefreshTrigger((prev) => prev + 1);
      } else {
        setOnDataAddModalResult(postResponce);
        setEntryAddData((prev) => ({ ...prev, addStatus: "contract" }));
      }
    }
  }

  // トスアップ: 登録処理
  async function postTossUpAddData(customerInfo: {
    [key: string]: string | string[];
  }) {
    const postResponce = await postTossUp({ ...tossUpAddData, customerInfo });
    if (postResponce === "success") {
      closeTossUpAddModal();
      setAddSuccessSnackbar("トスアップを登録しました");
      setRefreshTrigger((prev) => prev + 1);
    } else {
      setOnDataAddModalResult(postResponce);
      setTossUpAddData((prev) => ({ ...prev, addStatus: "toss_up" }));
    }
  }

  // エントリーモーダル閉じる
  function closeEntryAddModal() {
    setEntryAddData({
      addStatus: null,
      outSiteEntry: 0,
      outsideSiteUrl: "",
      outsideServiceUrl: "",
      viewOutsideServiceUrl: 0,
      productId: null,
      productName: "",
      have_entry_form: null,
      site_path_list: [],
      site_path: "",
      outsideMessageType: null,
      phone_number: "",
      customer_name: "",
      customer_company_name: "",
      servicePlanName: "",
      serviceContentNameList: [],
      serviceCampaignPulldownName: "",
      serviceCampaignCheckboxName: [],
      servicePaymentMethodName: "",
      serviseCustomerListID: "",
      pullDownServiceName: "",
      checkBoxServiceNames: "",
      smsMessagePatternID: null,
      customerInfo: {},
      changeAccount: 0,
      changeAccountID: null,
    });
    setEntryProductList([]);
    setEntrySelectServisePlanList([]);
    setEntrySelectServiseContentList([]);
    setEntrySelectServiseCampaignPulldownList([]);
    setEntrySelectServiseCampaignCheckboxList([]);
    setEntrySelectServisePaymentList([]);
    setEntryUniqueOptinList([]);
    setEntryAddressOptionName("");
    setEntryPostPicture({});
    setDashboardAddType(null);
  }

  // トスアップモーダル閉じる
  function closeTossUpAddModal() {
    setTossUpAddData({
      addStatus: null,
      category_id_list: [],
      product_name_list: [],
      phone_number: "",
      customer_name: "",
      customer_list_id: "",
      hope_call_date: "",
      hope_call_time_kind: null,
      hope_call_time: "12-00",
      customerInfo: {},
      changeAccount: 0,
      changeAccountID: null,
    });
    setTossUpCategoryNameList([]);
    setTossUpProductNameList([]);
    setTossUpUniqueOptinList([]);
    setTossUpAddressOptionName("");
    setTossUpPostPicture({});
    setDashboardAddType(null);
  }

  /** パートナー・MGRはトップ5表示、営業アカウント・トスアップアカウントはトップ5なし（トレンドのみ） */
  const showTop5Graphs =
    loginUser?.db_authority !== "営業アカウント" &&
    loginUser?.db_authority !== "トスアップアカウント";

  /** パートナー・MGRはグループ別、マスター・スタッフはパートナー別でトップ5を表示 */
  const useGroupDivision =
    loginUser?.db_authority === "パートナー" ||
    loginUser?.db_authority === "営業MGR";

  const sixMonthsAgo = (() => {
    const d = new Date(selectedKpiYear, selectedKpiMonth - 1);
    d.setMonth(d.getMonth() - 6);
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  })();

  /** 当月の日別ならKPIと同一期間に（ダッシュボードは 1日〜今日 なので end_day = 今日） */
  const withMonthToDateEndDay = (
    params: { [key: string]: string },
    year: number,
    month: number,
    granularity: string,
  ): { [key: string]: string } => {
    const now = new Date();
    if (
      granularity !== "日別" ||
      year !== now.getFullYear() ||
      month !== now.getMonth() + 1
    )
      return params;
    const lastDay = new Date(year, month, 0).getDate();
    const endDay = Math.min(now.getDate(), lastDay);
    return { ...params, analysys_granularity_date_end_day: String(endDay) };
  };

  /** エントリー分析: 当月日別なら 1日〜今日 の期間に制限（解約数は解約日基準のため entry_cancell_base は渡さない） */
  const entryParamsForDashboard = (
    params: { [key: string]: string },
    year: number,
    month: number,
  ): { [key: string]: string } => {
    const now = new Date();
    if (year !== now.getFullYear() || month !== now.getMonth() + 1)
      return params;
    const lastDay = new Date(year, month, 0).getDate();
    const endDay = Math.min(now.getDate(), lastDay);
    return { ...params, analysys_granularity_date_end_day: String(endDay) };
  };

  /** エントリー分析の計上日: 解約数は解約日、それ以外はシステム登録日 */
  const entryCountType: "解約日" | "システム登録日" =
    selectedEntryAggregation === "解約数" ? "解約日" : "システム登録日";

  useEffect(() => {
    setIsLoading(kpiLoading || graphLoading);
  }, [kpiLoading, graphLoading, setIsLoading]);

  useEffect(() => {
    if (!thisYear || !thisMonth) return;
    let cancelled = false;
    setKpiLoading(true);
    (async () => {
      const dateStr = `${selectedKpiDate.getFullYear()}-${String(selectedKpiDate.getMonth() + 1).padStart(2, "0")}-${String(selectedKpiDate.getDate()).padStart(2, "0")}`;
      const dashboardRes = await getDashboard({
        year: selectedKpiYear,
        month: selectedKpiMonth,
        date: dateStr,
      });
      if (!cancelled) {
        if (dashboardRes) setDashboardKpi(dashboardRes);
        setKpiLoading(false);
        setFirstLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    thisYear,
    thisMonth,
    selectedKpiYear,
    selectedKpiMonth,
    selectedKpiDate,
    refreshTrigger,
  ]);

  /** 選択中セクションのグラフデータのみ取得。カテゴリ切り替え時はいったんクリアしてから取得し、古いデータを表示しない */
  useEffect(() => {
    if (!thisYear || !thisMonth) return;
    // トップ5の表示区分は loginUser 次第のため、未読込時は取得しない（グループ別がアカウント別にならないようにする）
    if (selectedSectionForD === "tossup" && showTop5Graphs && loginUser == null)
      return;
    if (
      selectedSectionForD === "closing" &&
      (!isClosingAvailable || (showTop5Graphs && loginUser == null))
    )
      return;
    if (
      selectedSectionForD === "entry" &&
      (!isEntryAnalysisAvailable || (showTop5Graphs && loginUser == null))
    )
      return;

    if (selectedSectionForD === "tossup") {
      setTossUpByPartner([]);
      setTossUpByAccount([]);
      setTossUpTrendDaily([]);
      setTossUpTrendMonthly([]);
    } else if (selectedSectionForD === "closing") {
      setClosingByPartner([]);
      setClosingByAccount([]);
      setClosingTrendDaily([]);
      setClosingTrendMonthly([]);
    } else if (selectedSectionForD === "entry") {
      setEntryShotByProduct([]);
      setEntryShotByPartner([]);
      setEntryShotByAccount([]);
      setEntryShotTrendDaily([]);
      setEntryShotTrendMonthly([]);
    }

    let cancelled = false;
    const selectedSixMonthsAgo = (() => {
      const d = new Date(selectedKpiYear, selectedKpiMonth - 1);
      d.setMonth(d.getMonth() - 6);
      return { year: d.getFullYear(), month: d.getMonth() + 1 };
    })();
    (async () => {
      setGraphLoading(true);
      try {
        const sectionPromises: Promise<unknown>[] = [];
        if (selectedSectionForD === "tossup") {
          const tossUpFirstDivisionAdmin = useGroupDivision
            ? "トスアップグループ"
            : "トスアップパートナー";
          const tossUpFirstDivisionPartner = useGroupDivision
            ? "トスアップグループ"
            : "トスアップ担当者";
          const dailyCategories = showTop5Graphs
            ? [tossUpFirstDivisionAdmin, "トスアップ担当者"]
            : ["トスアップ担当者"];
          const dailyDivAdmin = showTop5Graphs ? tossUpFirstDivisionAdmin : "トスアップ担当者";
          const dailyDivPartner = showTop5Graphs ? tossUpFirstDivisionPartner : "トスアップ担当者";
          sectionPromises.push(
            getTossUpAnalysysMultiDivision(
              withMonthToDateEndDay(
                buildTossUpParams(
                  dailyDivAdmin,
                  dailyDivPartner,
                  "トスアップ日",
                  selectedTossUpSubCount,
                  selectedKpiYear,
                  selectedKpiMonth,
                  selectedKpiYear,
                  selectedKpiMonth,
                  "日別",
                ),
                selectedKpiYear,
                selectedKpiMonth,
                "日別",
              ),
              dailyCategories,
            ),
            getTossUpAnalysysMultiDivision(
              buildTossUpParams(
                showTop5Graphs ? tossUpFirstDivisionAdmin : "トスアップ担当者",
                showTop5Graphs ? tossUpFirstDivisionPartner : "トスアップ担当者",
                "トスアップ日",
                selectedTossUpSubCount,
                selectedSixMonthsAgo.year,
                selectedSixMonthsAgo.month,
                selectedKpiYear,
                selectedKpiMonth,
                "月別",
              ),
              showTop5Graphs ? [tossUpFirstDivisionAdmin] : ["トスアップ担当者"],
            ),
          );
        } else if (selectedSectionForD === "closing") {
          const closingFirstDivisionAdmin = useGroupDivision
            ? "クロージンググループ"
            : "クロージングパートナー";
          const closingFirstDivisionPartner = useGroupDivision
            ? "クロージンググループ"
            : "クロージング担当者";
          const dailyCategories = showTop5Graphs
            ? [closingFirstDivisionAdmin, "クロージング担当者"]
            : ["クロージング担当者"];
          const dailyDivAdmin = showTop5Graphs ? closingFirstDivisionAdmin : "クロージング担当者";
          const dailyDivPartner = showTop5Graphs ? closingFirstDivisionPartner : "クロージング担当者";
          sectionPromises.push(
            getTossUpAnalysysMultiDivision(
              withMonthToDateEndDay(
                buildTossUpParams(
                  dailyDivAdmin,
                  dailyDivPartner,
                  "クロージング日",
                  selectedClosingSubCount,
                  selectedKpiYear,
                  selectedKpiMonth,
                  selectedKpiYear,
                  selectedKpiMonth,
                  "日別",
                ),
                selectedKpiYear,
                selectedKpiMonth,
                "日別",
              ),
              dailyCategories,
            ),
            getTossUpAnalysysMultiDivision(
              buildTossUpParams(
                showTop5Graphs ? closingFirstDivisionAdmin : "クロージング担当者",
                showTop5Graphs ? closingFirstDivisionPartner : "クロージング担当者",
                "クロージング日",
                selectedClosingSubCount,
                selectedSixMonthsAgo.year,
                selectedSixMonthsAgo.month,
                selectedKpiYear,
                selectedKpiMonth,
                "月別",
              ),
              showTop5Graphs ? [closingFirstDivisionAdmin] : ["クロージング担当者"],
            ),
          );
        } else if (
          selectedSectionForD === "entry" &&
          isEntryAnalysisAvailable
        ) {
          const entryPartnerDiv = useGroupDivision ? "グループ" : "パートナー";
          const dailyCategories = showTop5Graphs
            ? ["商材", entryPartnerDiv, "営業アカウント"]
            : ["商材"];
          sectionPromises.push(
            getEntryAnalysysMultiDivision(
              entryParamsForDashboard(
                buildEntryParams(
                  "商材",
                  entryCountType,
                  selectedEntryAggregation,
                  selectedEntryPriceType,
                  selectedKpiYear,
                  selectedKpiMonth,
                  selectedKpiYear,
                  selectedKpiMonth,
                  "日別",
                ),
                selectedKpiYear,
                selectedKpiMonth,
              ),
              dailyCategories,
            ),
            getEntryAnalysysMultiDivision(
              buildEntryParams(
                "商材",
                entryCountType,
                selectedEntryAggregation,
                selectedEntryPriceType,
                selectedSixMonthsAgo.year,
                selectedSixMonthsAgo.month,
                selectedKpiYear,
                selectedKpiMonth,
                "月別",
              ),
              ["商材"],
            ),
          );
        }

        const rest = await Promise.all(sectionPromises);

        if (selectedSectionForD === "tossup" && rest.length >= 2) {
          type MultiResult = Awaited<ReturnType<typeof getTossUpAnalysysMultiDivision>>;
          const [dailyMulti, monthlyMulti] = rest as [MultiResult, MultiResult];
          const firstDiv = useGroupDivision
            ? "トスアップグループ"
            : "トスアップパートナー";
          if (showTop5Graphs) {
            const partnerData = dailyMulti?.[firstDiv];
            if (!cancelled && partnerData) {
              setTossUpByPartner(
                (partnerData.all_data || []).slice(0, 5).map((r) => ({
                  name: String(r.name ?? "その他"),
                  合計: Number(r.合計 ?? 0),
                })),
              );
              setTossUpTrendDaily(partnerData.analysys || []);
            }
            const accountData = dailyMulti?.["トスアップ担当者"];
            if (!cancelled && accountData) {
              setTossUpByAccount(
                (accountData.all_data || []).slice(0, 5).map((r) => ({
                  name: String(r.name ?? "その他"),
                  合計: Number(r.合計 ?? 0),
                })),
              );
            }
            const monthlyData = monthlyMulti?.[firstDiv];
            if (!cancelled && monthlyData) {
              setTossUpTrendMonthly(monthlyData.analysys || []);
            }
          } else {
            const dailyData = dailyMulti?.["トスアップ担当者"];
            if (!cancelled && dailyData)
              setTossUpTrendDaily(dailyData.analysys || []);
            const monthlyData = monthlyMulti?.["トスアップ担当者"];
            if (!cancelled && monthlyData)
              setTossUpTrendMonthly(monthlyData.analysys || []);
          }
        } else if (selectedSectionForD === "closing" && rest.length >= 2) {
          type MultiResult = Awaited<ReturnType<typeof getTossUpAnalysysMultiDivision>>;
          const [dailyMulti, monthlyMulti] = rest as [MultiResult, MultiResult];
          const firstDiv = useGroupDivision
            ? "クロージンググループ"
            : "クロージングパートナー";
          if (showTop5Graphs) {
            const partnerData = dailyMulti?.[firstDiv];
            if (!cancelled && partnerData) {
              setClosingByPartner(
                (partnerData.all_data || []).slice(0, 5).map((r) => ({
                  name: String(r.name ?? "その他"),
                  合計: Number(r.合計 ?? 0),
                })),
              );
              setClosingTrendDaily(partnerData.analysys || []);
            }
            const accountData = dailyMulti?.["クロージング担当者"];
            if (!cancelled && accountData) {
              setClosingByAccount(
                (accountData.all_data || []).slice(0, 5).map((r) => ({
                  name: String(r.name ?? "その他"),
                  合計: Number(r.合計 ?? 0),
                })),
              );
            }
            const monthlyData = monthlyMulti?.[firstDiv];
            if (!cancelled && monthlyData) {
              setClosingTrendMonthly(monthlyData.analysys || []);
            }
          } else {
            const dailyData = dailyMulti?.["クロージング担当者"];
            if (!cancelled && dailyData)
              setClosingTrendDaily(dailyData.analysys || []);
            const monthlyData = monthlyMulti?.["クロージング担当者"];
            if (!cancelled && monthlyData)
              setClosingTrendMonthly(monthlyData.analysys || []);
          }
        } else if (
          selectedSectionForD === "entry" &&
          isEntryAnalysisAvailable &&
          rest.length >= 2
        ) {
          type MultiResult = Awaited<ReturnType<typeof getEntryAnalysysMultiDivision>>;
          const [dailyMulti, monthlyMulti] = rest as [MultiResult, MultiResult];
          const entryPartnerDiv = useGroupDivision ? "グループ" : "パートナー";
          if (showTop5Graphs) {
            const toTop5 = (data: dataDifine[] | undefined) =>
              (data || []).slice(0, 5).map((r) => ({
                name: String(r.name ?? ""),
                合計: Number(r.合計 ?? 0),
              }));
            const productData = dailyMulti?.["商材"];
            if (!cancelled && productData) {
              setEntryShotByProduct(toTop5(productData.all_data));
              setEntryShotTrendDaily(productData.analysys || []);
            }
            const partnerData = dailyMulti?.[entryPartnerDiv];
            if (!cancelled && partnerData)
              setEntryShotByPartner(toTop5(partnerData.all_data));
            const accountData = dailyMulti?.["営業アカウント"];
            if (!cancelled && accountData)
              setEntryShotByAccount(toTop5(accountData.all_data));
            const monthlyData = monthlyMulti?.["商材"];
            if (!cancelled && monthlyData)
              setEntryShotTrendMonthly(monthlyData.analysys || []);
          } else {
            const dailyData = dailyMulti?.["商材"];
            if (!cancelled && dailyData) {
              setEntryShotTrendDaily(dailyData.analysys || []);
            }
            const monthlyData = monthlyMulti?.["商材"];
            if (!cancelled && monthlyData)
              setEntryShotTrendMonthly(monthlyData.analysys || []);
          }
        }
      } finally {
        if (!cancelled) setGraphLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    thisYear,
    thisMonth,
    selectedKpiYear,
    selectedKpiMonth,
    loginUser,
    isEntryAnalysisAvailable,
    showTop5Graphs,
    useGroupDivision,
    selectedSectionForD,
    selectedTossUpSubCount,
    selectedClosingSubCount,
    selectedEntryAggregation,
    selectedEntryPriceType,
    refreshTrigger,
  ]);

  const toSearchParams = (
    p: Record<string, unknown>,
  ): { [key: string]: string } =>
    Object.fromEntries(
      Object.entries(p).map(([k, v]) => [
        k,
        v != null && v !== undefined ? String(v) : "",
      ]),
    );

  const openDetail = async (
    type: AnalysisDetailModalType,
    initialParams?: Record<string, unknown>,
  ) => {
    const params = initialParams ? toSearchParams(initialParams) : null;
    if (!params) return;
    setDetailModalType(type);
    setDetailModalInitialParams(params);
    setDetailModalPreloadedData(null);
    const divAdmin =
      type === "entry"
        ? "entry_analysys_division_admin"
        : "toss_up_analysys_division_admin";
    const divPartner =
      type === "entry"
        ? "entry_analysys_division_partner"
        : "toss_up_analysys_division_partner";
    if (
      (params[divAdmin] || "").includes(",") ||
      (params[divPartner] || "").includes(",")
    ) {
      setDetailModalOpen(true);
      return;
    }
    setIsLoading(true);
    try {
      const result =
        type === "entry"
          ? await getEntryAnalysys(params)
          : await getTossUpAnalysys(params);
      setDetailModalPreloadedData({
        analysys: result?.analysys ?? [],
        columns: result?.columns ?? [],
        all_data: result?.all_data ?? [],
      });
      setDetailModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  /** カレンダー: 当月以外の月が選択された場合、当該月のデータをfetch */
  useEffect(() => {
    const isCurrentMonth =
      calendarViewYear === thisYear && calendarViewMonth === thisMonth;
    if (isCurrentMonth) {
      setCalendarOverrideData(null);
      return;
    }
    if (!thisYear || !thisMonth) return;
    let cancelled = false;
    (async () => {
      setCalendarDataLoading(true);
      try {
        if (selectedSectionForD === "entry" && isEntryAnalysisAvailable) {
          const result = await getEntryAnalysys(
            buildEntryParams(
              "商材",
              entryCountType,
              selectedEntryAggregation,
              selectedEntryPriceType,
              calendarViewYear,
              calendarViewMonth,
              calendarViewYear,
              calendarViewMonth,
              "日別",
            ),
          );
          if (!cancelled)
            setCalendarOverrideData(toTrendData(result?.analysys ?? []));
        } else if (selectedSectionForD === "tossup") {
          const result = await getTossUpAnalysys(
            buildTossUpParams(
              showTop5Graphs
                ? useGroupDivision
                  ? "トスアップグループ"
                  : "トスアップパートナー"
                : "トスアップ担当者",
              showTop5Graphs
                ? useGroupDivision
                  ? "トスアップグループ"
                  : "トスアップ担当者"
                : "トスアップ担当者",
              "トスアップ日",
              selectedTossUpSubCount,
              calendarViewYear,
              calendarViewMonth,
              calendarViewYear,
              calendarViewMonth,
              "日別",
            ),
          );
          if (!cancelled)
            setCalendarOverrideData(toTrendData(result?.analysys ?? []));
        } else if (selectedSectionForD === "closing" && isClosingAvailable) {
          const result = await getTossUpAnalysys(
            buildTossUpParams(
              showTop5Graphs
                ? useGroupDivision
                  ? "クロージンググループ"
                  : "クロージングパートナー"
                : "クロージング担当者",
              showTop5Graphs
                ? useGroupDivision
                  ? "クロージンググループ"
                  : "クロージング担当者"
                : "クロージング担当者",
              "クロージング日",
              selectedClosingSubCount,
              calendarViewYear,
              calendarViewMonth,
              calendarViewYear,
              calendarViewMonth,
              "日別",
            ),
          );
          if (!cancelled)
            setCalendarOverrideData(toTrendData(result?.analysys ?? []));
        }
      } finally {
        if (!cancelled) setCalendarDataLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    calendarViewYear,
    calendarViewMonth,
    selectedSectionForD,
    selectedTossUpSubCount,
    selectedClosingSubCount,
    selectedEntryAggregation,
    selectedEntryPriceType,
    thisYear,
    thisMonth,
    isEntryAnalysisAvailable,
    isClosingAvailable,
    showTop5Graphs,
    useGroupDivision,
    entryCountType,
  ]);

  /** KPI月ナビ: 「今月の実績」の月送り */
  const handleKpiMonthPrev = () => {
    const prev = new Date(selectedKpiYear, selectedKpiMonth - 2);
    setSelectedKpiYear(prev.getFullYear());
    setSelectedKpiMonth(prev.getMonth() + 1);
    setCalendarViewYear(prev.getFullYear());
    setCalendarViewMonth(prev.getMonth() + 1);
    setCalendarOverrideData(null);
  };
  const handleKpiMonthNext = () => {
    const next = new Date(selectedKpiYear, selectedKpiMonth);
    const now = new Date();
    if (
      next.getFullYear() > now.getFullYear() ||
      (next.getFullYear() === now.getFullYear() &&
        next.getMonth() + 1 > now.getMonth() + 1)
    )
      return;
    setSelectedKpiYear(next.getFullYear());
    setSelectedKpiMonth(next.getMonth() + 1);
    setCalendarViewYear(next.getFullYear());
    setCalendarViewMonth(next.getMonth() + 1);
    setCalendarOverrideData(null);
  };

  /** KPI日付ナビ: 「本日の実績」の日付送り */
  const handleKpiDatePrev = () => {
    const prev = new Date(selectedKpiDate);
    prev.setDate(prev.getDate() - 1);
    setSelectedKpiDate(prev);
  };
  const handleKpiDateNext = () => {
    const next = new Date(selectedKpiDate);
    next.setDate(next.getDate() + 1);
    const now = new Date();
    now.setHours(23, 59, 59, 999);
    if (next > now) return;
    setSelectedKpiDate(next);
  };

  /** カレンダー: 月送りナビゲーション */
  const handleCalendarPrev = () => {
    const prev = new Date(calendarViewYear, calendarViewMonth - 2);
    setCalendarViewYear(prev.getFullYear());
    setCalendarViewMonth(prev.getMonth() + 1);
  };
  const handleCalendarNext = () => {
    const next = new Date(calendarViewYear, calendarViewMonth);
    setCalendarViewYear(next.getFullYear());
    setCalendarViewMonth(next.getMonth() + 1);
  };

  /** セクション切替時にカレンダーを当月にリセット */
  const handleSectionChange = (newSection: "tossup" | "closing" | "entry") => {
    setSelectedSectionForD(newSection);
    const now = new Date();
    setCalendarViewYear(thisYear || now.getFullYear());
    setCalendarViewMonth(thisMonth || now.getMonth() + 1);
    setCalendarOverrideData(null);
  };

  /** カレンダーに渡す日別データ（当月 → 既存state、それ以外 → override） */
  const getCalendarData = (): { name: string; 合計: number }[] => {
    const isCurrentMonth =
      calendarViewYear === thisYear && calendarViewMonth === thisMonth;
    if (!isCurrentMonth) return calendarOverrideData ?? [];
    if (selectedSectionForD === "tossup") return toTrendData(tossUpTrendDaily);
    if (selectedSectionForD === "closing")
      return toTrendData(closingTrendDaily);
    return toTrendData(entryShotTrendDaily);
  };

  /** 商材ドリルダウン: 商材バーをクリック時にパートナー/アカウント別データ＋日別カレンダーデータをfetch */
  const handleProductClick = async (productName: string) => {
    setDrillDownProductName(productName);
    setDrillDownOpen(true);
    setDrillDownLoading(true);
    setDrillDownDailyData([]);
    try {
      const baseParams = buildEntryParams(
        "パートナー",
        entryCountType,
        selectedEntryAggregation,
        selectedEntryPriceType,
        selectedKpiYear,
        selectedKpiMonth,
        selectedKpiYear,
        selectedKpiMonth,
        "日別",
      );
      const accountParams = buildEntryParams(
        "営業アカウント",
        entryCountType,
        selectedEntryAggregation,
        selectedEntryPriceType,
        selectedKpiYear,
        selectedKpiMonth,
        selectedKpiYear,
        selectedKpiMonth,
        "日別",
      );
      const dailyParams = entryParamsForDashboard(
        buildEntryParams(
          "商材",
          entryCountType,
          selectedEntryAggregation,
          selectedEntryPriceType,
          selectedKpiYear,
          selectedKpiMonth,
          selectedKpiYear,
          selectedKpiMonth,
          "日別",
        ),
        selectedKpiYear,
        selectedKpiMonth,
      );
      const [partnerRes, accountRes, dailyRes] = await Promise.all([
        getEntryAnalysys({ ...baseParams, product_name: productName }),
        getEntryAnalysys({ ...accountParams, product_name: productName }),
        getEntryAnalysys({ ...dailyParams, product_name: productName }),
      ]);
      setDrillDownByPartner(
        (partnerRes?.all_data ?? []).slice(0, 10).map((r) => ({
          name: String(r.name ?? ""),
          合計: Number(r.合計 ?? 0),
        })),
      );
      setDrillDownByAccount(
        (accountRes?.all_data ?? []).slice(0, 10).map((r) => ({
          name: String(r.name ?? ""),
          合計: Number(r.合計 ?? 0),
        })),
      );
      setDrillDownDailyData(toTrendData(dailyRes?.analysys ?? []));
    } finally {
      setDrillDownLoading(false);
    }
  };

  /** エントリー パートナーバークリック → 日別カレンダー + 補完ランキング */
  const handleEntryPartnerClick = (partnerName: string) => {
    openDetail("entry", {
      ...buildEntryParams(
        "商材,営業アカウント",
        entryCountType,
        selectedEntryAggregation,
        selectedEntryPriceType,
        selectedKpiYear,
        selectedKpiMonth,
        selectedKpiYear,
        selectedKpiMonth,
        "日別",
      ),
      sale_company_name: partnerName,
    });
  };

  /** エントリー 営業アカウントバークリック → 日別カレンダー + 商材ランキング */
  const handleEntryAccountClick = (accountName: string) => {
    openDetail("entry", {
      ...buildEntryParams(
        "商材",
        entryCountType,
        selectedEntryAggregation,
        selectedEntryPriceType,
        selectedKpiYear,
        selectedKpiMonth,
        selectedKpiYear,
        selectedKpiMonth,
        "日別",
      ),
      sale_account_name: accountName,
    });
  };

  /** カレンダー 日付クリック → 日別詳細ダイアログ */
  const handleCalendarDayClick = async (day: number) => {
    const dateStr = `${calendarViewYear}-${String(calendarViewMonth).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    setDayDetailDate(dateStr);
    setDayDetailOpen(true);
    setDayDetailLoading(true);
    setDayDetailData(null);
    const res = await getDashboardDailyDetail({ date: dateStr });
    setDayDetailData(res);
    setDayDetailLoading(false);
  };

  /** 月別グラフ バークリック → カレンダーをその月に切替（B5） */
  const handleMonthBarClick = (
    data: { name: string },
    type: AnalysisDetailModalType,
  ) => {
    // name 例: "2026年3月" → year=2026, month=3
    const yearMatch = data.name.match(/(\d{4})年/);
    const monthMatch = data.name.match(/年(\d+)月/);
    if (!yearMatch || !monthMatch) return;
    const y = parseInt(yearMatch[1], 10);
    const m = parseInt(monthMatch[1], 10);
    if (type === "entry") {
      openDetail(
        "entry",
        buildEntryParams(
          loginUser?.authority === "マスターアカウント" ||
            loginUser?.authority === "スタッフアカウント"
            ? "商材,パートナー,営業アカウント"
            : "商材,営業アカウント",
          entryCountType,
          selectedEntryAggregation,
          selectedEntryPriceType,
          y,
          m,
          y,
          m,
          "日別",
        ),
      );
    } else if (type === "tossup") {
      openDetail(
        "tossup",
        buildTossUpParams(
          showTop5Graphs
            ? useGroupDivision
              ? "トスアップグループ,トスアップ担当者"
              : "トスアップパートナー,トスアップ担当者"
            : "トスアップ担当者",
          showTop5Graphs
            ? useGroupDivision
              ? "トスアップグループ,トスアップ担当者"
              : "トスアップ担当者"
            : "トスアップ担当者",
          "トスアップ日",
          selectedTossUpSubCount,
          y,
          m,
          y,
          m,
          "日別",
        ),
      );
    } else {
      openDetail(
        "closing",
        buildTossUpParams(
          showTop5Graphs
            ? useGroupDivision
              ? "クロージンググループ,クロージング担当者"
              : "クロージングパートナー,クロージング担当者"
            : "クロージング担当者",
          showTop5Graphs
            ? useGroupDivision
              ? "クロージンググループ,クロージング担当者"
              : "クロージング担当者"
            : "クロージング担当者",
          "クロージング日",
          selectedClosingSubCount,
          y,
          m,
          y,
          m,
          "日別",
        ),
      );
    }
  };

  const kpiForDisplay: DashboardResponse =
    dashboardKpi ?? getEmptyDashboardResponse(daysElapsed, daysInMonth);

  return (
    <Box
      style={{
        width: isSmartPhone
          ? "calc(100% - 31px)"
          : aiChatOpen
            ? "calc(100% - 331px)"
            : "calc(100% - 31px)",
        height: isSmartPhone ? "calc(100vh - 80px)" : "calc(100vh - 40px)",
        marginTop: isSmartPhone ? "80px" : undefined,
      }}
    >
      <TableTopBar
        serch={false}
        serchOpen={serchOpen}
        setSerchOpen={setSerchOpen}
        addDataNames={[]}
        addDataName=""
        setAddDataName={() => {}}
        addCSVDataNames={[]}
        addCSVDataName=""
        setAddCSVDataName={() => {}}
        getXLSXDataNames={[]}
        getXLSXDataName=""
        setGetXLSXDataName={() => {}}
        editDatasNames={[]}
        editDatasName=""
        setEditDatasName={() => {}}
        editIDs={[]}
        columnSettingOpen={false}
        openColumnSettingWindow={() => {}}
        aiChatOpen={aiChatOpen}
        setAiChatOpen={setAiChatOpen}
      />
      <Box style={{ display: "flex", width: "calc(100% + 31px)" }}>
        <Box
          sx={{
            flex: 1,
            overflowX: "hidden",
            overflowY: "auto",
            height: isSmartPhone ? "calc(100vh - 80px)" : "calc(100vh - 80px)",
          }}
        >
          <Box
            sx={{
              p: 2,
              maxWidth: 1600,
              margin: "0 auto",
              boxSizing: "border-box",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Typography variant="h6" className="shippori-antique-regular">
                分析ダッシュボード
              </Typography>
              {canAddEntry && (
                <Box sx={{ display: "flex", gap: 1 }}>
                  {isEntryAnalysisAvailable && (
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleOpenEntryAdd}
                      sx={{
                        color: "#c45c26",
                        borderColor: "#c45c26",
                        "&:hover": {
                          borderColor: "#a34d20",
                          backgroundColor: "rgba(196,92,38,0.08)",
                        },
                        fontWeight: 600,
                      }}
                    >
                      エントリー
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleOpenTossUpAdd}
                    sx={{ fontWeight: 600 }}
                  >
                    トスアップ
                  </Button>
                </Box>
              )}
            </Box>

            {/* 本日の成果（当日・前日） */}
            <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={handleKpiDatePrev}
                sx={{ minWidth: 28, px: 0.5, py: 0.3 }}
              >
                {"<"}
              </Button>
              <Typography variant="subtitle1" fontWeight={600}>
                {selectedKpiDate.toDateString() === new Date().toDateString()
                  ? "本日の実績"
                  : `${selectedKpiDate.getMonth() + 1}月${selectedKpiDate.getDate()}日の実績`}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={handleKpiDateNext}
                disabled={
                  selectedKpiDate.toDateString() === new Date().toDateString()
                    ? true
                    : false
                }
                sx={{ minWidth: 28, px: 0.5, py: 0.3 }}
              >
                {">"}
              </Button>
            </Box>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {[
                ...(isEntryAnalysisAvailable
                  ? [
                      {
                        label: "エントリー（ショット）",
                        borderLeftColor: "#c45c26",
                        today: kpiForDisplay.today?.entry_number_shot ?? 0,
                        yesterday:
                          kpiForDisplay.yesterday?.entry_number_shot ?? 0,
                      },
                      {
                        label: "エントリー（ストック）",
                        borderLeftColor: "#c45c26",
                        today: kpiForDisplay.today?.entry_number_stack ?? 0,
                        yesterday:
                          kpiForDisplay.yesterday?.entry_number_stack ?? 0,
                      },
                    ]
                  : []),
                {
                  label: "トスアップ数",
                  borderLeftColor: "primary.main",
                  today: kpiForDisplay.today?.toss_up_number ?? 0,
                  yesterday: kpiForDisplay.yesterday?.toss_up_number ?? 0,
                },
                ...(isClosingAvailable
                  ? [
                      {
                        label: "クロージング-有効アポ数",
                        borderLeftColor: "#02844a",
                        today: kpiForDisplay.today?.closing_appoint_number ?? 0,
                        yesterday:
                          kpiForDisplay.yesterday?.closing_appoint_number ?? 0,
                      },
                    ]
                  : []),
              ].map(({ label, borderLeftColor, today, yesterday }) => (
                <Grid item xs={12} sm={6} md={3} key={label}>
                  <Card
                    variant="outlined"
                    sx={{
                      height: "100%",
                      borderLeft: "4px solid",
                      borderLeftColor,
                    }}
                  >
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        {label}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <Typography component="span" variant="h6">
                          当日: {today}
                        </Typography>
                        <Typography
                          component="span"
                          variant="h6"
                          color="textSecondary"
                        >
                          前日: {yesterday}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* 今月の成果 */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mt: 3,
                mb: 2,
                gap: 1,
              }}
            >
              <Button
                size="small"
                variant="outlined"
                onClick={handleKpiMonthPrev}
                sx={{ minWidth: 28, px: 0.5, py: 0.3 }}
              >
                {"<"}
              </Button>
              <Typography variant="subtitle1" fontWeight={600}>
                {selectedKpiYear === (thisYear || new Date().getFullYear()) &&
                selectedKpiMonth === (thisMonth || new Date().getMonth() + 1)
                  ? "今月の実績"
                  : `${selectedKpiYear}年${selectedKpiMonth}月の実績`}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={handleKpiMonthNext}
                disabled={
                  selectedKpiYear === thisYear && selectedKpiMonth === thisMonth
                }
                sx={{ minWidth: 28, px: 0.5, py: 0.3 }}
              >
                {">"}
              </Button>
            </Box>

            {/* 今月の成果: カード一覧 + 選択中カードのグラフのみ */}
            <Box sx={{ mb: 3 }}>
              <Tabs
                value={selectedSectionForD}
                onChange={(_e, v) => handleSectionChange(v)}
                sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
              >
                {isEntryAnalysisAvailable && (
                  <Tab label="エントリー" value="entry" />
                )}
                <Tab label="トスアップ" value="tossup" />
                {isClosingAvailable && (
                  <Tab label="クロージング" value="closing" />
                )}
              </Tabs>
              <Grid container spacing={1} sx={{ mb: 2 }} alignItems="center">
                {selectedSectionForD === "tossup" &&
                  [
                    {
                      section: "tossup" as const,
                      subCount: "トスアップ数",
                      label: "トスアップ数",
                      value: kpiForDisplay.month_to_date.toss_up_number,
                      last: kpiForDisplay.last_month.toss_up_number,
                      borderLeftColor: "primary.main",
                    },
                    {
                      section: "tossup" as const,
                      subCount: "トスアップ-有効アポ数",
                      label: "トスアップ-有効アポ数",
                      value:
                        kpiForDisplay.month_to_date
                          .toss_up_valid_appoint_number,
                      last: kpiForDisplay.last_month
                        .toss_up_valid_appoint_number,
                      borderLeftColor: "primary.main",
                    },
                    {
                      section: "tossup" as const,
                      subCount: "トスアップ-前確OK数",
                      label: "トスアップ-前確OK数",
                      value:
                        kpiForDisplay.month_to_date.toss_up_pre_check_ok_number,
                      last: kpiForDisplay.last_month
                        .toss_up_pre_check_ok_number,
                      borderLeftColor: "primary.main",
                    },
                    {
                      section: "tossup" as const,
                      subCount: "トスアップ-開通数",
                      label: "トスアップ-開通数",
                      value: kpiForDisplay.month_to_date.toss_up_open_number,
                      last: kpiForDisplay.last_month.toss_up_open_number,
                      borderLeftColor: "primary.main",
                    },
                  ].map((item) => {
                    const selected =
                      selectedSectionForD === "tossup" &&
                      selectedTossUpSubCount === item.subCount;
                    return (
                      <Grid item xs={6} sm={4} md={3} key={item.label}>
                        <Card
                          variant="outlined"
                          onClick={() => {
                            setSelectedSectionForD("tossup");
                            setSelectedTossUpSubCount(item.subCount);
                          }}
                          sx={{
                            borderLeft: "4px solid",
                            borderLeftColor: item.borderLeftColor,
                            cursor: "pointer",
                            bgcolor: selected ? "action.selected" : undefined,
                            boxShadow: selected ? 2 : 0,
                            "&:hover": {
                              bgcolor: selected
                                ? "action.selected"
                                : "action.hover",
                            },
                          }}
                        >
                          <CardContent
                            sx={{ py: 1, "&:last-child": { pb: 1 } }}
                          >
                            <Typography
                              color="textSecondary"
                              variant="body2"
                              gutterBottom
                              noWrap
                              title={item.label}
                            >
                              {item.label}
                            </Typography>
                            <Typography variant="h6">{item.value}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              前月: {item.last} / 見込:{" "}
                              {getProratedTotal(
                                item.value,
                                kpiForDisplay.days_elapsed,
                                kpiForDisplay.days_in_month,
                              )}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                {selectedSectionForD === "closing" && isClosingAvailable && (
                  <>
                    <Grid item xs={12} sx={{ mt: 1 }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        sx={{ color: "#02844a" }}
                      >
                        クロージング
                      </Typography>
                    </Grid>
                    {[
                      {
                        section: "closing" as const,
                        subCount: "クロージング-有効アポ数",
                        label: "クロージング-有効アポ数",
                        value:
                          kpiForDisplay.month_to_date.closing_appoint_number,
                        last: kpiForDisplay.last_month.closing_appoint_number,
                        borderLeftColor: "#02844a",
                      },
                      {
                        section: "closing" as const,
                        subCount: "クロージング-エントリー数",
                        label: "クロージング-エントリー数",
                        value:
                          kpiForDisplay.month_to_date.closing_success_number,
                        last: kpiForDisplay.last_month.closing_success_number,
                        borderLeftColor: "#02844a",
                      },
                      {
                        section: "closing" as const,
                        subCount: "クロージング-開通数",
                        label: "クロージング-開通数",
                        value: kpiForDisplay.month_to_date.closing_open_number,
                        last: kpiForDisplay.last_month.closing_open_number,
                        borderLeftColor: "#02844a",
                      },
                    ].map((item) => {
                      const selected =
                        selectedSectionForD === "closing" &&
                        selectedClosingSubCount === item.subCount;
                      return (
                        <Grid item xs={6} sm={4} md={3} key={item.label}>
                          <Card
                            variant="outlined"
                            onClick={() => {
                              setSelectedSectionForD("closing");
                              setSelectedClosingSubCount(item.subCount);
                            }}
                            sx={{
                              borderLeft: "4px solid",
                              borderLeftColor: item.borderLeftColor,
                              cursor: "pointer",
                              bgcolor: selected ? "action.selected" : undefined,
                              boxShadow: selected ? 2 : 0,
                              "&:hover": {
                                bgcolor: selected
                                  ? "action.selected"
                                  : "action.hover",
                              },
                            }}
                          >
                            <CardContent
                              sx={{ py: 1, "&:last-child": { pb: 1 } }}
                            >
                              <Typography
                                color="textSecondary"
                                variant="body2"
                                gutterBottom
                                noWrap
                                title={item.label}
                              >
                                {item.label}
                              </Typography>
                              <Typography variant="h6">{item.value}</Typography>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                前月: {item.last} / 見込:{" "}
                                {getProratedTotal(
                                  item.value,
                                  kpiForDisplay.days_elapsed,
                                  kpiForDisplay.days_in_month,
                                )}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </>
                )}
                {selectedSectionForD === "entry" &&
                  isEntryAnalysisAvailable && (
                    <>
                      <Grid item xs={12} sx={{ mt: 1 }}>
                        <Typography
                          variant="subtitle1"
                          fontWeight={600}
                          sx={{ color: "#c45c26" }}
                        >
                          エントリー
                        </Typography>
                      </Grid>
                      {[
                        {
                          section: "entry" as const,
                          label: "エントリー（ショット）",
                          value: kpiForDisplay.month_to_date.entry_number_shot,
                          last: kpiForDisplay.last_month.entry_number_shot,
                          borderLeftColor: "#c45c26",
                          agg: "エントリー数" as const,
                          price: "ショット" as const,
                        },
                        {
                          section: "entry" as const,
                          label: "エントリー（ストック）",
                          value: kpiForDisplay.month_to_date.entry_number_stack,
                          last: kpiForDisplay.last_month.entry_number_stack,
                          borderLeftColor: "#c45c26",
                          agg: "エントリー数" as const,
                          price: "ストック" as const,
                        },
                        {
                          section: "entry" as const,
                          label: "エントリー（ショット）-開通数",
                          value:
                            kpiForDisplay.month_to_date.entry_shot_open_number,
                          last: kpiForDisplay.last_month.entry_shot_open_number,
                          borderLeftColor: "#c45c26",
                          agg: "開通数" as const,
                          price: "ショット" as const,
                        },
                        {
                          section: "entry" as const,
                          label: "エントリー（ストック）-開通数",
                          value:
                            kpiForDisplay.month_to_date.entry_stack_open_number,
                          last: kpiForDisplay.last_month
                            .entry_stack_open_number,
                          borderLeftColor: "#c45c26",
                          agg: "開通数" as const,
                          price: "ストック" as const,
                        },
                        {
                          section: "entry" as const,
                          label: "解約数（ショット）",
                          value:
                            kpiForDisplay.month_to_date
                              .entry_shot_cancell_number,
                          last: kpiForDisplay.last_month
                            .entry_shot_cancell_number,
                          borderLeftColor: "#c45c26",
                          agg: "解約数" as const,
                          price: "ショット" as const,
                        },
                        {
                          section: "entry" as const,
                          label: "解約数（ストック）",
                          value:
                            kpiForDisplay.month_to_date
                              .entry_stack_cancell_number,
                          last: kpiForDisplay.last_month
                            .entry_stack_cancell_number,
                          borderLeftColor: "#c45c26",
                          agg: "解約数" as const,
                          price: "ストック" as const,
                        },
                      ].map((item) => {
                        const selected =
                          selectedSectionForD === "entry" &&
                          selectedEntryAggregation === item.agg &&
                          selectedEntryPriceType === item.price;
                        return (
                          <Grid item xs={6} sm={4} md={3} key={item.label}>
                            <Card
                              variant="outlined"
                              onClick={() => {
                                setSelectedSectionForD("entry");
                                setSelectedEntryAggregation(item.agg);
                                setSelectedEntryPriceType(item.price);
                              }}
                              sx={{
                                borderLeft: "4px solid",
                                borderLeftColor: item.borderLeftColor,
                                cursor: "pointer",
                                bgcolor: selected
                                  ? "action.selected"
                                  : undefined,
                                boxShadow: selected ? 2 : 0,
                                "&:hover": {
                                  bgcolor: selected
                                    ? "action.selected"
                                    : "action.hover",
                                },
                              }}
                            >
                              <CardContent
                                sx={{ py: 1, "&:last-child": { pb: 1 } }}
                              >
                                <Typography
                                  color="textSecondary"
                                  variant="body2"
                                  gutterBottom
                                  noWrap
                                  title={item.label}
                                >
                                  {item.label}
                                </Typography>
                                <Typography variant="h6">
                                  {item.value}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                >
                                  前月: {item.last} / 見込:{" "}
                                  {getProratedTotal(
                                    item.value,
                                    kpiForDisplay.days_elapsed,
                                    kpiForDisplay.days_in_month,
                                  )}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                    </>
                  )}
              </Grid>
            </Box>

            {/* 選択中セクションのグラフのみ表示 */}
            {selectedSectionForD === "tossup" && (
              <Grid container spacing={2} sx={{ mb: 4 }}>
                {showTop5Graphs && (
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: "100%" }}>
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                          トスアップ{" "}
                          {useGroupDivision ? "グループ" : "パートナー"}別
                          トップ5
                        </Typography>
                        <Box sx={{ height: 260 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              layout="vertical"
                              data={tossUpByPartner}
                              margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis type="number" />
                              <YAxis
                                type="category"
                                dataKey="name"
                                width={180}
                                tick={(p) => (
                                  <CustomizedYAxisTick
                                    {...p}
                                    labelWidth={172}
                                    fontSize={12}
                                    alignLeftInAxis
                                  />
                                )}
                              />
                              <Tooltip />
                              <Bar
                                dataKey="合計"
                                cursor="pointer"
                                onClick={(data) =>
                                  openDetail("tossup", {
                                    ...buildTossUpParams(
                                      "トスアップ担当者",
                                      "トスアップ担当者",
                                      "トスアップ日",
                                      selectedTossUpSubCount,
                                      selectedKpiYear,
                                      selectedKpiMonth,
                                      selectedKpiYear,
                                      selectedKpiMonth,
                                      "日別",
                                    ),
                                    [useGroupDivision
                                      ? "toss_up_company_group_name"
                                      : "toss_up_company_name"]: String(
                                      data.name ?? "",
                                    ),
                                  })
                                }
                              >
                                {tossUpByPartner.map((_, i) => (
                                  <Cell
                                    key={i}
                                    fill={colorPlette[i % colorPlette.length]}
                                  />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </Box>
                        <Button
                          size="small"
                          color="primary"
                          onClick={() =>
                            openDetail(
                              "tossup",
                              buildTossUpParams(
                                useGroupDivision
                                  ? "トスアップグループ,トスアップ担当者"
                                  : "トスアップパートナー,トスアップ担当者",
                                useGroupDivision
                                  ? "トスアップグループ,トスアップ担当者"
                                  : "トスアップ担当者",
                                "トスアップ日",
                                selectedTossUpSubCount,
                                selectedKpiYear,
                                selectedKpiMonth,
                                selectedKpiYear,
                                selectedKpiMonth,
                                "日別",
                              ),
                            )
                          }
                          sx={{ mt: 1 }}
                        >
                          詳細を見る
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {showTop5Graphs && (
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: "100%" }}>
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                          トスアップアカウント別 トップ5
                        </Typography>
                        <Box sx={{ height: 260 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              layout="vertical"
                              data={tossUpByAccount}
                              margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis type="number" />
                              <YAxis
                                type="category"
                                dataKey="name"
                                width={180}
                                tick={(p) => (
                                  <CustomizedYAxisTick
                                    {...p}
                                    labelWidth={172}
                                    fontSize={12}
                                    alignLeftInAxis
                                  />
                                )}
                              />
                              <Tooltip />
                              <Bar
                                dataKey="合計"
                                cursor="pointer"
                                onClick={(data) =>
                                  openDetail("tossup", {
                                    ...buildTossUpParams(
                                      useGroupDivision
                                        ? "トスアップグループ"
                                        : "トスアップパートナー",
                                      useGroupDivision
                                        ? "トスアップグループ"
                                        : "トスアップ担当者",
                                      "トスアップ日",
                                      selectedTossUpSubCount,
                                      selectedKpiYear,
                                      selectedKpiMonth,
                                      selectedKpiYear,
                                      selectedKpiMonth,
                                      "日別",
                                    ),
                                    toss_up_account_name: String(
                                      data.name ?? "",
                                    ),
                                  })
                                }
                              >
                                {tossUpByAccount.map((_, i) => (
                                  <Cell
                                    key={i}
                                    fill={colorPlette[i % colorPlette.length]}
                                  />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </Box>
                        <Button
                          size="small"
                          color="primary"
                          onClick={() =>
                            openDetail(
                              "tossup",
                              buildTossUpParams(
                                useGroupDivision
                                  ? "トスアップグループ,トスアップ担当者"
                                  : "トスアップパートナー,トスアップ担当者",
                                useGroupDivision
                                  ? "トスアップグループ,トスアップ担当者"
                                  : "トスアップ担当者",
                                "トスアップ日",
                                selectedTossUpSubCount,
                                selectedKpiYear,
                                selectedKpiMonth,
                                selectedKpiYear,
                                selectedKpiMonth,
                                "日別",
                              ),
                            )
                          }
                          sx={{ mt: 1 }}
                        >
                          詳細を見る
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ height: "100%" }}>
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>
                        {selectedTossUpSubCount} 日別推移
                      </Typography>
                      {calendarDataLoading ? (
                        <Box
                          sx={{
                            height: 240,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Typography variant="body2" color="textSecondary">
                            読込中...
                          </Typography>
                        </Box>
                      ) : (
                        <DailyCalendar
                          year={calendarViewYear}
                          month={calendarViewMonth}
                          data={getCalendarData()}
                          onPrev={handleCalendarPrev}
                          onNext={handleCalendarNext}
                          onDayClick={handleCalendarDayClick}
                        />
                      )}
                      <Button
                        size="small"
                        color="primary"
                        onClick={() =>
                          openDetail(
                            "tossup",
                            buildTossUpParams(
                              showTop5Graphs
                                ? useGroupDivision
                                  ? "トスアップグループ,トスアップ担当者"
                                  : "トスアップパートナー,トスアップ担当者"
                                : "トスアップ担当者",
                              showTop5Graphs
                                ? useGroupDivision
                                  ? "トスアップグループ,トスアップ担当者"
                                  : "トスアップ担当者"
                                : "トスアップ担当者",
                              "トスアップ日",
                              selectedTossUpSubCount,
                              selectedKpiYear,
                              selectedKpiMonth,
                              selectedKpiYear,
                              selectedKpiMonth,
                              "日別",
                            ),
                          )
                        }
                        sx={{ mt: 1 }}
                      >
                        詳細を見る
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ height: "100%" }}>
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>
                        {selectedTossUpSubCount} 月別推移（直近6ヶ月）
                      </Typography>
                      <Box sx={{ height: 240 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={toTrendData(tossUpTrendMonthly)}
                            margin={{ top: 5, right: 10, left: 0, bottom: 20 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} />
                            <Tooltip />
                            <Bar
                              dataKey="合計"
                              fill="#03AF7A"
                              cursor="pointer"
                              onClick={(data) =>
                                handleMonthBarClick(
                                  data as { name: string },
                                  "tossup",
                                )
                              }
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                      <Button
                        size="small"
                        color="primary"
                        onClick={() =>
                          openDetail(
                            "tossup",
                            buildTossUpParams(
                              showTop5Graphs
                                ? useGroupDivision
                                  ? "トスアップグループ,トスアップ担当者"
                                  : "トスアップパートナー,トスアップ担当者"
                                : "トスアップ担当者",
                              showTop5Graphs
                                ? useGroupDivision
                                  ? "トスアップグループ,トスアップ担当者"
                                  : "トスアップ担当者"
                                : "トスアップ担当者",
                              "トスアップ日",
                              selectedTossUpSubCount,
                              sixMonthsAgo.year,
                              sixMonthsAgo.month,
                              selectedKpiYear,
                              selectedKpiMonth,
                              "月別",
                            ),
                          )
                        }
                        sx={{ mt: 1 }}
                      >
                        詳細を見る
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}

            {selectedSectionForD === "closing" && isClosingAvailable && (
              <Grid container spacing={2} sx={{ mb: 4 }}>
                {showTop5Graphs && (
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: "100%" }}>
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                          クロージング{" "}
                          {useGroupDivision ? "グループ" : "パートナー"}別
                          トップ5
                        </Typography>
                        <Box sx={{ height: 260 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              layout="vertical"
                              data={closingByPartner}
                              margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis type="number" />
                              <YAxis
                                type="category"
                                dataKey="name"
                                width={180}
                                tick={(p) => (
                                  <CustomizedYAxisTick
                                    {...p}
                                    labelWidth={172}
                                    fontSize={12}
                                    alignLeftInAxis
                                  />
                                )}
                              />
                              <Tooltip />
                              <Bar
                                dataKey="合計"
                                cursor="pointer"
                                onClick={(data) =>
                                  openDetail("closing", {
                                    ...buildTossUpParams(
                                      "クロージング担当者",
                                      "クロージング担当者",
                                      "クロージング日",
                                      selectedClosingSubCount,
                                      selectedKpiYear,
                                      selectedKpiMonth,
                                      selectedKpiYear,
                                      selectedKpiMonth,
                                      "日別",
                                    ),
                                    [useGroupDivision
                                      ? "closer_company_group_name"
                                      : "closer_company_name"]: String(
                                      data.name ?? "",
                                    ),
                                  })
                                }
                              >
                                {closingByPartner.map((_, i) => (
                                  <Cell
                                    key={i}
                                    fill={colorPlette[i % colorPlette.length]}
                                  />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </Box>
                        <Button
                          size="small"
                          color="primary"
                          onClick={() =>
                            openDetail(
                              "closing",
                              buildTossUpParams(
                                useGroupDivision
                                  ? "クロージンググループ,クロージング担当者"
                                  : "クロージングパートナー,クロージング担当者",
                                useGroupDivision
                                  ? "クロージンググループ,クロージング担当者"
                                  : "クロージング担当者",
                                "クロージング日",
                                selectedClosingSubCount,
                                selectedKpiYear,
                                selectedKpiMonth,
                                selectedKpiYear,
                                selectedKpiMonth,
                                "日別",
                              ),
                            )
                          }
                          sx={{ mt: 1 }}
                        >
                          詳細を見る
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {showTop5Graphs && (
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: "100%" }}>
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                          クロージングアカウント別 トップ5
                        </Typography>
                        <Box sx={{ height: 260 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              layout="vertical"
                              data={closingByAccount}
                              margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis type="number" />
                              <YAxis
                                type="category"
                                dataKey="name"
                                width={180}
                                tick={(p) => (
                                  <CustomizedYAxisTick
                                    {...p}
                                    labelWidth={172}
                                    fontSize={12}
                                    alignLeftInAxis
                                  />
                                )}
                              />
                              <Tooltip />
                              <Bar
                                dataKey="合計"
                                cursor="pointer"
                                onClick={(data) =>
                                  openDetail("closing", {
                                    ...buildTossUpParams(
                                      useGroupDivision
                                        ? "クロージンググループ"
                                        : "クロージングパートナー",
                                      useGroupDivision
                                        ? "クロージンググループ"
                                        : "クロージング担当者",
                                      "クロージング日",
                                      selectedClosingSubCount,
                                      selectedKpiYear,
                                      selectedKpiMonth,
                                      selectedKpiYear,
                                      selectedKpiMonth,
                                      "日別",
                                    ),
                                    closer_account_name: String(
                                      data.name ?? "",
                                    ),
                                  })
                                }
                              >
                                {closingByAccount.map((_, i) => (
                                  <Cell
                                    key={i}
                                    fill={colorPlette[i % colorPlette.length]}
                                  />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </Box>
                        <Button
                          size="small"
                          color="primary"
                          onClick={() =>
                            openDetail(
                              "closing",
                              buildTossUpParams(
                                useGroupDivision
                                  ? "クロージンググループ,クロージング担当者"
                                  : "クロージングパートナー,クロージング担当者",
                                useGroupDivision
                                  ? "クロージンググループ,クロージング担当者"
                                  : "クロージング担当者",
                                "クロージング日",
                                selectedClosingSubCount,
                                selectedKpiYear,
                                selectedKpiMonth,
                                selectedKpiYear,
                                selectedKpiMonth,
                                "日別",
                              ),
                            )
                          }
                          sx={{ mt: 1 }}
                        >
                          詳細を見る
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ height: "100%" }}>
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>
                        {selectedClosingSubCount} 日別推移
                      </Typography>
                      {calendarDataLoading ? (
                        <Box
                          sx={{
                            height: 240,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Typography variant="body2" color="textSecondary">
                            読込中...
                          </Typography>
                        </Box>
                      ) : (
                        <DailyCalendar
                          year={calendarViewYear}
                          month={calendarViewMonth}
                          data={getCalendarData()}
                          onPrev={handleCalendarPrev}
                          onNext={handleCalendarNext}
                          onDayClick={handleCalendarDayClick}
                        />
                      )}
                      <Button
                        size="small"
                        color="primary"
                        onClick={() =>
                          openDetail(
                            "closing",
                            buildTossUpParams(
                              showTop5Graphs
                                ? useGroupDivision
                                  ? "クロージンググループ,クロージング担当者"
                                  : "クロージングパートナー,クロージング担当者"
                                : "クロージング担当者",
                              showTop5Graphs
                                ? useGroupDivision
                                  ? "クロージンググループ,クロージング担当者"
                                  : "クロージング担当者"
                                : "クロージング担当者",
                              "クロージング日",
                              selectedClosingSubCount,
                              selectedKpiYear,
                              selectedKpiMonth,
                              selectedKpiYear,
                              selectedKpiMonth,
                              "日別",
                            ),
                          )
                        }
                        sx={{ mt: 1 }}
                      >
                        詳細を見る
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ height: "100%" }}>
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>
                        {selectedClosingSubCount} 月別推移（直近6ヶ月）
                      </Typography>
                      <Box sx={{ height: 240 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={toTrendData(closingTrendMonthly)}
                            margin={{ top: 5, right: 10, left: 0, bottom: 20 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} />
                            <Tooltip />
                            <Bar
                              dataKey="合計"
                              fill="#03AF7A"
                              cursor="pointer"
                              onClick={(data) =>
                                handleMonthBarClick(
                                  data as { name: string },
                                  "closing",
                                )
                              }
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                      <Button
                        size="small"
                        color="primary"
                        onClick={() =>
                          openDetail(
                            "closing",
                            buildTossUpParams(
                              showTop5Graphs
                                ? useGroupDivision
                                  ? "クロージンググループ,クロージング担当者"
                                  : "クロージングパートナー,クロージング担当者"
                                : "クロージング担当者",
                              showTop5Graphs
                                ? useGroupDivision
                                  ? "クロージンググループ,クロージング担当者"
                                  : "クロージング担当者"
                                : "クロージング担当者",
                              "クロージング日",
                              selectedClosingSubCount,
                              sixMonthsAgo.year,
                              sixMonthsAgo.month,
                              selectedKpiYear,
                              selectedKpiMonth,
                              "月別",
                            ),
                          )
                        }
                        sx={{ mt: 1 }}
                      >
                        詳細を見る
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}

            {selectedSectionForD === "entry" && isEntryAnalysisAvailable && (
              <>
                <Grid container spacing={2} sx={{ mb: 4 }}>
                  {showTop5Graphs && (
                    <>
                      <Grid item xs={12} md={4}>
                        <Card variant="outlined" sx={{ height: "100%" }}>
                          <CardContent>
                            <Typography variant="subtitle2" gutterBottom>
                              商材別 トップ5
                            </Typography>
                            <Box sx={{ height: 260 }}>
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                  layout="vertical"
                                  data={entryShotByProduct}
                                  margin={{
                                    top: 5,
                                    right: 20,
                                    left: 0,
                                    bottom: 5,
                                  }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis type="number" />
                                  <YAxis
                                    type="category"
                                    dataKey="name"
                                    width={180}
                                    tick={(p) => (
                                      <CustomizedYAxisTick
                                        {...p}
                                        labelWidth={172}
                                        fontSize={12}
                                        alignLeftInAxis
                                      />
                                    )}
                                  />
                                  <Tooltip />
                                  <Bar
                                    dataKey="合計"
                                    cursor="pointer"
                                    onClick={(data) =>
                                      openDetail("entry", {
                                        ...buildEntryParams(
                                          "パートナー,営業アカウント",
                                          entryCountType,
                                          selectedEntryAggregation,
                                          selectedEntryPriceType,
                                          selectedKpiYear,
                                          selectedKpiMonth,
                                          selectedKpiYear,
                                          selectedKpiMonth,
                                          "日別",
                                        ),
                                        product_name: String(data.name ?? ""),
                                      })
                                    }
                                  >
                                    {entryShotByProduct.map((_, i) => (
                                      <Cell
                                        key={i}
                                        fill={
                                          colorPlette[i % colorPlette.length]
                                        }
                                      />
                                    ))}
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            </Box>
                            <Button
                              size="small"
                              color="primary"
                              onClick={() =>
                                openDetail(
                                  "entry",
                                  buildEntryParams(
                                    loginUser?.authority ===
                                      "マスターアカウント" ||
                                      loginUser?.authority ===
                                        "スタッフアカウント"
                                      ? "商材,パートナー,営業アカウント"
                                      : "商材,営業アカウント",
                                    entryCountType,
                                    selectedEntryAggregation,
                                    selectedEntryPriceType,
                                    selectedKpiYear,
                                    selectedKpiMonth,
                                    selectedKpiYear,
                                    selectedKpiMonth,
                                    "日別",
                                  ),
                                )
                              }
                              sx={{ mt: 1 }}
                            >
                              詳細を見る
                            </Button>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Card variant="outlined" sx={{ height: "100%" }}>
                          <CardContent>
                            <Typography variant="subtitle2" gutterBottom>
                              {useGroupDivision ? "グループ" : "パートナー"}別
                              トップ5
                            </Typography>
                            <Box sx={{ height: 260 }}>
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                  layout="vertical"
                                  data={entryShotByPartner}
                                  margin={{
                                    top: 5,
                                    right: 20,
                                    left: 0,
                                    bottom: 5,
                                  }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis type="number" />
                                  <YAxis
                                    type="category"
                                    dataKey="name"
                                    width={180}
                                    tick={(p) => (
                                      <CustomizedYAxisTick
                                        {...p}
                                        labelWidth={172}
                                        fontSize={12}
                                        alignLeftInAxis
                                      />
                                    )}
                                  />
                                  <Tooltip />
                                  <Bar
                                    dataKey="合計"
                                    cursor="pointer"
                                    onClick={(data) =>
                                      handleEntryPartnerClick(
                                        String(data.name ?? ""),
                                      )
                                    }
                                  >
                                    {entryShotByPartner.map((_, i) => (
                                      <Cell
                                        key={i}
                                        fill={
                                          colorPlette[i % colorPlette.length]
                                        }
                                      />
                                    ))}
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            </Box>
                            <Button
                              size="small"
                              color="primary"
                              onClick={() =>
                                openDetail(
                                  "entry",
                                  buildEntryParams(
                                    loginUser?.authority ===
                                      "マスターアカウント" ||
                                      loginUser?.authority ===
                                        "スタッフアカウント"
                                      ? "商材,パートナー,営業アカウント"
                                      : "商材,営業アカウント",
                                    entryCountType,
                                    selectedEntryAggregation,
                                    selectedEntryPriceType,
                                    selectedKpiYear,
                                    selectedKpiMonth,
                                    selectedKpiYear,
                                    selectedKpiMonth,
                                    "日別",
                                  ),
                                )
                              }
                              sx={{ mt: 1 }}
                            >
                              詳細を見る
                            </Button>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Card variant="outlined" sx={{ height: "100%" }}>
                          <CardContent>
                            <Typography variant="subtitle2" gutterBottom>
                              営業アカウント別 トップ5
                            </Typography>
                            <Box sx={{ height: 260 }}>
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                  layout="vertical"
                                  data={entryShotByAccount}
                                  margin={{
                                    top: 5,
                                    right: 20,
                                    left: 0,
                                    bottom: 5,
                                  }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis type="number" />
                                  <YAxis
                                    type="category"
                                    dataKey="name"
                                    width={180}
                                    tick={(p) => (
                                      <CustomizedYAxisTick
                                        {...p}
                                        labelWidth={172}
                                        fontSize={12}
                                        alignLeftInAxis
                                      />
                                    )}
                                  />
                                  <Tooltip />
                                  <Bar
                                    dataKey="合計"
                                    cursor="pointer"
                                    onClick={(data) =>
                                      handleEntryAccountClick(
                                        String(data.name ?? ""),
                                      )
                                    }
                                  >
                                    {entryShotByAccount.map((_, i) => (
                                      <Cell
                                        key={i}
                                        fill={
                                          colorPlette[i % colorPlette.length]
                                        }
                                      />
                                    ))}
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            </Box>
                            <Button
                              size="small"
                              color="primary"
                              onClick={() =>
                                openDetail(
                                  "entry",
                                  buildEntryParams(
                                    loginUser?.authority ===
                                      "マスターアカウント" ||
                                      loginUser?.authority ===
                                        "スタッフアカウント"
                                      ? "商材,パートナー,営業アカウント"
                                      : "商材,営業アカウント",
                                    entryCountType,
                                    selectedEntryAggregation,
                                    selectedEntryPriceType,
                                    selectedKpiYear,
                                    selectedKpiMonth,
                                    selectedKpiYear,
                                    selectedKpiMonth,
                                    "日別",
                                  ),
                                )
                              }
                              sx={{ mt: 1 }}
                            >
                              詳細を見る
                            </Button>
                          </CardContent>
                        </Card>
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: "100%" }}>
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                          {selectedEntryAggregation}（{selectedEntryPriceType}
                          ）日別推移
                        </Typography>
                        {calendarDataLoading ? (
                          <Box
                            sx={{
                              height: 240,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Typography variant="body2" color="textSecondary">
                              読込中...
                            </Typography>
                          </Box>
                        ) : (
                          <DailyCalendar
                            year={calendarViewYear}
                            month={calendarViewMonth}
                            data={getCalendarData()}
                            onPrev={handleCalendarPrev}
                            onNext={handleCalendarNext}
                            onDayClick={handleCalendarDayClick}
                          />
                        )}
                        <Button
                          size="small"
                          color="primary"
                          onClick={() =>
                            openDetail(
                              "entry",
                              buildEntryParams(
                                loginUser?.authority === "マスターアカウント" ||
                                  loginUser?.authority === "スタッフアカウント"
                                  ? "商材,パートナー,営業アカウント"
                                  : "商材,営業アカウント",
                                entryCountType,
                                selectedEntryAggregation,
                                selectedEntryPriceType,
                                selectedKpiYear,
                                selectedKpiMonth,
                                selectedKpiYear,
                                selectedKpiMonth,
                                "日別",
                              ),
                            )
                          }
                          sx={{ mt: 1 }}
                        >
                          詳細を見る
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: "100%" }}>
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                          {selectedEntryAggregation}（{selectedEntryPriceType}
                          ）月別推移（直近6ヶ月）
                        </Typography>
                        <Box sx={{ height: 240 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={toTrendData(entryShotTrendMonthly)}
                              margin={{
                                top: 5,
                                right: 10,
                                left: 0,
                                bottom: 20,
                              }}
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                              <YAxis tick={{ fontSize: 10 }} />
                              <Tooltip />
                              <Bar
                                dataKey="合計"
                                fill="#03AF7A"
                                cursor="pointer"
                                onClick={(data) =>
                                  handleMonthBarClick(
                                    data as { name: string },
                                    "entry",
                                  )
                                }
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </Box>
                        <Button
                          size="small"
                          color="primary"
                          onClick={() =>
                            openDetail(
                              "entry",
                              buildEntryParams(
                                loginUser?.authority === "マスターアカウント" ||
                                  loginUser?.authority === "スタッフアカウント"
                                  ? "商材,パートナー,営業アカウント"
                                  : "商材,営業アカウント",
                                entryCountType,
                                selectedEntryAggregation,
                                selectedEntryPriceType,
                                sixMonthsAgo.year,
                                sixMonthsAgo.month,
                                selectedKpiYear,
                                selectedKpiMonth,
                                "月別",
                              ),
                            )
                          }
                          sx={{ mt: 1 }}
                        >
                          詳細を見る
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </>
            )}

            {/* その他 */}
            {loginUser?.db_authority !== "営業アカウント" &&
              loginUser?.db_authority !== "トスアップアカウント" && (
                <Typography
                  variant="subtitle1"
                  className="shippori-antique-regular"
                  sx={{ mt: 2, mb: 1 }}
                >
                  その他
                </Typography>
              )}
            {loginUser?.db_authority !== "営業アカウント" &&
              loginUser?.db_authority !== "トスアップアカウント" && (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={4}>
                    <Card variant="outlined" sx={{ height: "100%" }}>
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                          アカウント別ランキング
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          日別・月別のトスアップ数、前確OK率、エントリー数、開通数などのランキング一覧はランキング画面で確認できます。
                        </Typography>
                        <Button
                          size="small"
                          color="primary"
                          component={Link}
                          to="/runking"
                          sx={{ mt: 1 }}
                        >
                          ランキング画面へ
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

            {/* 商材ドリルダウン モーダル */}
            <Dialog
              open={drillDownOpen}
              onClose={() => setDrillDownOpen(false)}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>
                【{drillDownProductName}】内訳
                <Typography
                  component="span"
                  variant="caption"
                  color="textSecondary"
                  sx={{ ml: 1 }}
                >
                  {selectedEntryAggregation}（{selectedEntryPriceType}）当月
                </Typography>
              </DialogTitle>
              <DialogContent>
                {drillDownLoading ? (
                  <Box sx={{ py: 4, textAlign: "center" }}>
                    <Typography color="textSecondary">読込中...</Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom>
                        日別推移
                      </Typography>
                      <DailyCalendar
                        year={thisYear || new Date().getFullYear()}
                        month={thisMonth || new Date().getMonth() + 1}
                        data={drillDownDailyData}
                        onPrev={() => {}}
                        onNext={() => {}}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>
                        {useGroupDivision ? "グループ" : "パートナー"}別
                      </Typography>
                      <Box sx={{ height: 300 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            layout="vertical"
                            data={drillDownByPartner}
                            margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis
                              type="category"
                              dataKey="name"
                              width={180}
                              tick={(p) => (
                                <CustomizedYAxisTick
                                  {...p}
                                  labelWidth={172}
                                  fontSize={12}
                                  alignLeftInAxis
                                />
                              )}
                            />
                            <Tooltip />
                            <Bar dataKey="合計">
                              {drillDownByPartner.map((_, i) => (
                                <Cell
                                  key={i}
                                  fill={colorPlette[i % colorPlette.length]}
                                />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>
                        営業アカウント別
                      </Typography>
                      <Box sx={{ height: 300 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            layout="vertical"
                            data={drillDownByAccount}
                            margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis
                              type="category"
                              dataKey="name"
                              width={180}
                              tick={(p) => (
                                <CustomizedYAxisTick
                                  {...p}
                                  labelWidth={172}
                                  fontSize={12}
                                  alignLeftInAxis
                                />
                              )}
                            />
                            <Tooltip />
                            <Bar dataKey="合計">
                              {drillDownByAccount.map((_, i) => (
                                <Cell
                                  key={i}
                                  fill={colorPlette[i % colorPlette.length]}
                                />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Grid>
                  </Grid>
                )}
              </DialogContent>
            </Dialog>

            {/* 日付クリック 日別詳細ダイアログ */}
            <Dialog
              open={dayDetailOpen}
              onClose={() => setDayDetailOpen(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>{dayDetailDate} の実績</DialogTitle>
              <DialogContent>
                {dayDetailLoading ? (
                  <Box sx={{ py: 4, textAlign: "center" }}>
                    <Typography color="textSecondary">読込中...</Typography>
                  </Box>
                ) : dayDetailData ? (
                  <Grid container spacing={1} sx={{ mt: 0.5 }}>
                    {isEntryAnalysisAvailable && (
                      <>
                        <Grid item xs={6}>
                          <Card variant="outlined">
                            <CardContent
                              sx={{ py: 1, "&:last-child": { pb: 1 } }}
                            >
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                エントリー（ショット）
                              </Typography>
                              <Typography variant="h6">
                                {dayDetailData.entry_number_shot}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                開通: {dayDetailData.entry_shot_open_number}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={6}>
                          <Card variant="outlined">
                            <CardContent
                              sx={{ py: 1, "&:last-child": { pb: 1 } }}
                            >
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                エントリー（ストック）
                              </Typography>
                              <Typography variant="h6">
                                {dayDetailData.entry_number_stack}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                開通: {dayDetailData.entry_stack_open_number}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      </>
                    )}
                    <Grid item xs={6}>
                      <Card variant="outlined">
                        <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
                          <Typography variant="caption" color="textSecondary">
                            トスアップ数
                          </Typography>
                          <Typography variant="h6">
                            {dayDetailData.toss_up_number}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            有効アポ:{" "}
                            {dayDetailData.toss_up_valid_appoint_number}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    {isClosingAvailable && (
                      <Grid item xs={6}>
                        <Card variant="outlined">
                          <CardContent
                            sx={{ py: 1, "&:last-child": { pb: 1 } }}
                          >
                            <Typography variant="caption" color="textSecondary">
                              クロージング-有効アポ数
                            </Typography>
                            <Typography variant="h6">
                              {dayDetailData.closing_appoint_number}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              エントリー: {dayDetailData.closing_success_number}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                  </Grid>
                ) : (
                  <Typography color="textSecondary" sx={{ py: 2 }}>
                    データなし
                  </Typography>
                )}
              </DialogContent>
            </Dialog>

            <AnalysisDetailModal
              open={detailModalOpen}
              onClose={() => {
                setDetailModalOpen(false);
                setDetailModalPreloadedData(null);
              }}
              type={detailModalType}
              initialSearchParams={detailModalInitialParams}
              initialData={detailModalPreloadedData}
            />
          </Box>
        </Box>
        {aiChatOpen && (
          <AiChatSideBar
            open={aiChatOpen}
            setOpen={setAiChatOpen}
            pageName="Dashboard"
          />
        )}
      </Box>

      {/* エントリー追加: アカウント選択モーダル */}
      <ChangeAccountCollaborateModal
        onModal={
          dashboardAddType === "entry" &&
          entryAddData.addStatus === "change_account"
        }
        swCloseModal={() => closeEntryAddModal()}
        changeAccount={entryAddData.changeAccount}
        changeAccountID={entryAddData.changeAccountID}
        setChangeAccount={(changeAccount: 0 | 1) =>
          setEntryAddData((prev) => ({ ...prev, changeAccount }))
        }
        setChangeAccountID={(changeAccountID: number | null) =>
          setEntryAddData((prev) => ({ ...prev, changeAccountID }))
        }
        accountCollaborateCompanySaleList={accountCollaborateCompanySaleList}
        title={"エントリー"}
        getFormFunction={getEntryProductListForDashboard}
        accountColumn={"営業担当アカウント"}
      />

      {/* エントリー追加モーダル */}
      <ContractAddModal
        onModal={
          dashboardAddType === "entry" &&
          entryAddData.addStatus !== null &&
          entryAddData.addStatus !== "loading" &&
          entryAddData.addStatus !== "change_account"
        }
        swCloseModal={() => closeEntryAddModal()}
        tableColumn={contractTableColumn}
        postAddData={postEntryAddData}
        addData={entryAddData}
        setAddData={setEntryAddData}
        contractProductList={entryProductList}
        firstYear={firstYear}
        thisYear={thisYear}
        selectServisePlanList={entrySelectServisePlanList}
        setSelectServisePlanList={setEntrySelectServisePlanList}
        selectServiseContentList={entrySelectServiseContentList}
        setSelectServiseContentList={setEntrySelectServiseContentList}
        selectServiseCampaignPulldownList={
          entrySelectServiseCampaignPulldownList
        }
        setSelectServiseCampaignPulldownList={
          setEntrySelectServiseCampaignPulldownList
        }
        selectServiseCampaignCheckboxList={
          entrySelectServiseCampaignCheckboxList
        }
        setSelectServiseCampaignCheckboxList={
          setEntrySelectServiseCampaignCheckboxList
        }
        selectServisePaymentList={entrySelectServisePaymentList}
        setSelectServisePaymentList={setEntrySelectServisePaymentList}
        uniqueOptinList={entryUniqueOptinList}
        setUniqueOptinList={setEntryUniqueOptinList}
        addressOptionName={entryAddressOptionName}
        setAddressOptionName={setEntryAddressOptionName}
        postPictures={entryPostPictures}
        setPostPicture={setEntryPostPicture}
        setOnDataAddModalResult={setOnDataAddModalResult}
      />

      {/* トスアップ追加: アカウント選択モーダル */}
      <ChangeAccountCollaborateModal
        onModal={
          dashboardAddType === "tossup" &&
          tossUpAddData.addStatus === "change_account"
        }
        swCloseModal={() => closeTossUpAddModal()}
        changeAccount={tossUpAddData.changeAccount}
        changeAccountID={tossUpAddData.changeAccountID}
        setChangeAccount={(changeAccount: 0 | 1) =>
          setTossUpAddData((prev) => ({ ...prev, changeAccount }))
        }
        setChangeAccountID={(changeAccountID: number | null) =>
          setTossUpAddData((prev) => ({ ...prev, changeAccountID }))
        }
        accountCollaborateCompanySaleList={accountCollaborateCompanySaleList}
        title={"トスアップ"}
        getFormFunction={getTossUpProductListForDashboard}
        accountColumn={"トスアップアカウント"}
      />

      {/* トスアップ追加モーダル */}
      <TossUpAddModal
        onModal={
          dashboardAddType === "tossup" &&
          tossUpAddData.addStatus !== null &&
          tossUpAddData.addStatus !== "loading" &&
          tossUpAddData.addStatus !== "change_account"
        }
        swCloseModal={() => closeTossUpAddModal()}
        postAddData={postTossUpAddData}
        addData={tossUpAddData}
        setAddData={setTossUpAddData}
        uniqueOptinList={tossUpUniqueOptinList}
        setUniqueOptinList={setTossUpUniqueOptinList}
        addressOptionName={tossUpAddressOptionName}
        setAddressOptionName={setTossUpAddressOptionName}
        postPictures={tossUpPostPictures}
        setPostPicture={setTossUpPostPicture}
        tossUpCategoryNameList={tossUpCategoryNameList}
        tossUpProductNameList={tossUpProductNameList}
        setOnDataAddModalResult={setOnDataAddModalResult}
      />

      {/* 登録結果モーダル */}
      <DataAddModalResult
        onModal={onDataAddModalResult !== ""}
        swCloseModal={() => {
          setOnDataAddModalResult("");
        }}
        addModalTitle={"新規作成"}
        onDataAddModalResult={onDataAddModalResult}
      />

      {/* 登録成功スナックバー */}
      <Snackbar
        open={addSuccessSnackbar !== null}
        autoHideDuration={4000}
        onClose={() => setAddSuccessSnackbar(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setAddSuccessSnackbar(null)}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {addSuccessSnackbar}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Dashboard;
